import { isAxiosError } from "axios";
import { api } from "@/api";
import {
  type IPOADokobitConfirmRequest,
  type IPOADokobitConsentRequest,
  type IPOADokobitConsentResponse,
  type IPoaJbsData,
  type IPOARecommendationsRequest,
  type IPOARecommendationsResponse,
  type IPOARikServiceProviderRequest,
  type IPOARikServiceProviderResponse,
  type IPOASignedDocumentMetadata,
  type IPOASignedDocumentResponse,
  type IPOAStatementConsentsBankLinkRequest,
  type IPOAStatementConsentsBankLinkResponse,
  type IPOAStatementConsentsBanksResponse,
  type IPOAStatementConsentsRecommendationsRequest,
  type IPOAStatementConsentsStateResponse,
  type IPOAStatementConsentsStatusRequest,
  type IPOAStatementConsentsStatusResponse,
  type ISubmitPOAServiceProviderRequestsRequest,
  type ISubmitUnderpermissionedExistingConsentRequest,
  type ISubmitUnderpermissionedExistingConsentResponse,
  type IUnderpermissionedExistingConsents,
  type TgetPOASignedDocument,
  type TgetPOASignedDocumentMetadata,
  type TgetPOAStatementConsentsStatus,
} from "./types";

export const getPOARecommendations = async (data: IPOARecommendationsRequest) =>
  (
    await api.get<IPOARecommendationsResponse>(
      `/powers-of-attorney/recommendations/service-provider`,
      { headers: { jointBillServiceId: data.jointBillServiceId } },
    )
  ).data;

export const getPOAStatementConsentsBanks = async () =>
  (
    await api.get<IPOAStatementConsentsBanksResponse>(
      "/powers-of-attorney/statement-consents/banks",
    )
  ).data;

export const getPOAStatementConsentsState = async () =>
  (
    await api.get<IPOAStatementConsentsStateResponse>(
      "/powers-of-attorney/statement-consents/states",
    )
  ).data;

export const getPOAStatementConsentsBankLink = async (
  data: IPOAStatementConsentsBankLinkRequest,
) =>
  (
    await api.post<IPOAStatementConsentsBankLinkResponse>(
      `/powers-of-attorney/statement-consents/${data.bankId}`,
    )
  ).data;

export const getPOAStatementConsentsStatus = async (
  data: IPOAStatementConsentsStatusRequest,
): TgetPOAStatementConsentsStatus => {
  try {
    const response = await api.get<IPOAStatementConsentsStatusResponse>(
      `/powers-of-attorney/statement-consents/${data.powerOfAttorneyLedgerConsentId}`,
      {
        timeout: 20_000,
      },
    );

    return { ...response.data, succeeded: true };
  } catch (error) {
    if (
      isAxiosError(error) &&
      (error.response?.status === 404 || error.response?.status === 500)
    ) {
      return { succeeded: false };
    }
    throw error;
  }
};

export const getPOAStatementConsentsRecommendations = async (
  data: IPOAStatementConsentsRecommendationsRequest,
) =>
  (
    await api.get<IPOARecommendationsResponse>(
      `/powers-of-attorney/statement-consents/${data.powerOfAttorneyLedgerConsentId}/service-provider-recommendations`,
      { headers: { jointBillServiceId: data.jointBillServiceId } },
    )
  ).data;

export const getPoaJbsData = () =>
  api
    .get<{
      jointBillServiceServiceProviderRequests: IPoaJbsData[];
    }>(`/powers-of-attorney/service-provider-requests`)
    .then((r) => r.data.jointBillServiceServiceProviderRequests);

export const submitPOAServiceProviderRequests = async (
  data: ISubmitPOAServiceProviderRequestsRequest,
) => {
  return await api.post("/powers-of-attorney/service-provider-requests", data);
};

export const getPOASignedDocumentMetadata =
  async (): TgetPOASignedDocumentMetadata => {
    try {
      const response = await api.get<IPOASignedDocumentMetadata>(
        "/powers-of-attorney/signed-document/metadata",
      );

      return { ...response.data, succeeded: true };
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return { succeeded: false };
      }
      throw error;
    }
  };

export const getPOARikServiceProvider = async (
  data: IPOARikServiceProviderRequest,
) =>
  (
    await api.get<IPOARikServiceProviderResponse>(
      "/powers-of-attorney/rik/service-provider",
      {
        params: data,
      },
    )
  ).data;

export const getPOASignedDocument = async (): TgetPOASignedDocument => {
  try {
    const response = await api.get<IPOASignedDocumentResponse>(
      "/powers-of-attorney/signed-document",
    );

    return { ...response.data, succeeded: true };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return { succeeded: false };
    }
    throw error;
  }
};

export const submitPOADokobitConsent = async (
  data: IPOADokobitConsentRequest,
) =>
  (
    await api.post<IPOADokobitConsentResponse>(
      "/powers-of-attorney/consent",
      data,
    )
  ).data;

export const submitPOADokobitConfirm = async (
  data: IPOADokobitConfirmRequest,
) => await api.post<void>("/powers-of-attorney/confirmation", data);

export const getAllPOAStatementConsentsRecommendations = async (
  jointBillServiceId: IPOAStatementConsentsRecommendationsRequest["jointBillServiceId"],
) =>
  (
    await api.get<IPOARecommendationsResponse>(
      "/powers-of-attorney/statement-consents/service-provider-recommendations",
      { headers: { jointBillServiceId } },
    )
  ).data;

export const getUnderpermissionedExistingConsents = async () => {
  const data = (
    await api.get<IUnderpermissionedExistingConsents>(
      "/powers-of-attorney/statement-consents/existing-underpermissioned",
    )
  ).data;

  return data;
};

export const submitUnderpermissionedExistingConsent = async (
  data: ISubmitUnderpermissionedExistingConsentRequest,
) =>
  (
    await api.post<ISubmitUnderpermissionedExistingConsentResponse>(
      `/powers-of-attorney/statement-consents/consent-acquirance/${data.ledgerConsentId}`,
    )
  ).data;
